<!--
    Pantalla de carga oficial de House & Flats (adaptada de
    primeras_tareas/pantalla-de-carga-demo.html): el logo naranja, una línea
    blanca que lo recorre por dentro y dos aviones 3D que dan vueltas
    alrededor mientras dura la carga.

    Liviana a propósito (03/11): el logo es vectorial (hyfLogoPaths.js, sin
    PNG ni canvas), el fondo es fijo (sin las manchas con blur animado de
    HyFFondo) y los aviones no llevan filtros que se recalculen en cada cuadro.

    Es la que usan todos los loaders del sitio (Loader/Loader, Ui/Loader,
    Loader/tinySpinner, Loader/DynamicLoader). Para una carga nueva, usar
    este componente directamente:

        <HyFLoader v-if="cargando" />                       pantalla completa
        <HyFLoader v-if="cargando" variant="overlay" />     tapa al padre (position: relative)
        <HyFLoader v-if="cargando" variant="inline" />      bloque dentro de la página
        <HyFLoader v-if="cargando" message="Enviando…" />   texto debajo del logo (o usar el slot)

    Al desaparecer, la versión de pantalla completa sube como un telón y
    deja ver la página, igual que en la demo.

    MODO PÁGINA (automático): mientras una página carga, en pantalla solo
    quedan el header y el loader. Cualquier HyFLoader (fullscreen, inline u
    overlay) se lleva a <body>, se fija debajo del header, tapa el resto del
    visor con el fondo de marca y, mientras está, se ocultan el footer, el
    botón de WhatsApp y el aviso de cookies y se bloquea el scroll (clase
    `hyf-page-loading` en <html>). Así el footer nunca "sube" ni se ve
    contenido a medio cargar, en ninguna página.
    No pasa dentro de un diálogo/panel modal ([role=dialog], [aria-modal],
    .modal): ahí el loader sigue dentro de su caja. Para una carga chica
    dentro de una página ya mostrada (p. ej. los mensajes de un chat):

        <HyFLoader v-if="cargando" variant="inline" local />
-->
<template>
    <!-- La raíz es solo un ancla (display: contents). En modo página la caja
         del loader se lleva a <body>: así ningún padre con transform /
         animación / backdrop-filter la deja atrapada (position: fixed
         quedaría relativo a ese padre y no a la pantalla). -->
    <div class="hyf-loader-anchor">
    <div
        ref="box"
        class="hyf-loader"
        :class="['hyf-loader--' + variant, { 'is-paused': paused, 'is-page': page }]"
        :style="page ? { '--hyf-ld-top': headerBottom + 'px' } : null"
        role="status"
        aria-live="polite"
        :aria-label="ariaLabel"
    >
        <!-- Modo página: fondo de marca fijo (los mismos tonos que HyFFondo, sin animar) -->
        <div v-if="page" class="hyf-loader__bg" aria-hidden="true"></div>
        <template v-else-if="variant !== 'inline'">
            <div class="hyf-loader__glow hyf-loader__glow--a" aria-hidden="true"></div>
            <div class="hyf-loader__glow hyf-loader__glow--c" aria-hidden="true"></div>
        </template>

        <div class="hyf-loader__stage">
            <div class="hyf-loader__mark-wrap">
                <!-- Logo naranja con una línea blanca que lo cruza. El viewBox deja el mismo margen
                     que tenía el PNG: los aviones orbitan alrededor de esta caja. -->
                <svg class="hyf-loader__logo" viewBox="-37 -39 471 135" role="img" aria-label="House and Flats" focusable="false">
                    <defs>
                        <linearGradient :id="uid('shine')" gradientUnits="userSpaceOnUse" x1="-70" y1="0" x2="0" y2="0">
                            <stop offset="0" stop-color="#FC7010" />
                            <stop offset=".5" stop-color="#fff" />
                            <stop offset="1" stop-color="#FC7010" />
                            <animateTransform v-if="brilla" attributeName="gradientTransform" type="translate" from="0 0" to="470 0" dur="1.4s" repeatCount="indefinite" />
                        </linearGradient>
                    </defs>
                    <g :fill="brilla ? url('shine') : '#FC7010'">
                        <path v-for="(d, i) in logoPaths" :key="i" :d="d" />
                    </g>
                </svg>

                <div v-if="!paused" class="hyf-loader__flight" aria-hidden="true">
                    <!-- Degradados y silueta cenital del avión (A320, en metros, morro hacia +x) -->
                    <svg class="hyf-loader__defs" focusable="false">
                        <defs>
                            <linearGradient :id="uid('fus')" gradientUnits="userSpaceOnUse" x1="0" y1="-2" x2="0" y2="2">
                                <stop offset="0" stop-color="#1a222c" />
                                <stop offset=".32" stop-color="#3f4b59" />
                                <stop offset=".5" stop-color="#4d5a6a" />
                                <stop offset=".7" stop-color="#333e4b" />
                                <stop offset="1" stop-color="#12181f" />
                            </linearGradient>
                            <linearGradient :id="uid('wing')" gradientUnits="userSpaceOnUse" x1="7" y1="0" x2="-4" y2="0">
                                <stop offset="0" stop-color="#3a4553" />
                                <stop offset="1" stop-color="#232b36" />
                            </linearGradient>
                            <linearGradient :id="uid('tail')" gradientUnits="userSpaceOnUse" x1="-14" y1="0" x2="-19.5" y2="0">
                                <stop offset="0" stop-color="#354050" />
                                <stop offset="1" stop-color="#1f2731" />
                            </linearGradient>
                            <linearGradient :id="uid('eng')" gradientUnits="userSpaceOnUse" x1="0" y1="-1.2" x2="0" y2="1.2">
                                <stop offset="0" stop-color="#0f141a" />
                                <stop offset=".5" stop-color="#2c3541" />
                                <stop offset="1" stop-color="#0a0e13" />
                            </linearGradient>
                            <linearGradient :id="uid('brand')" x1="0" y1="1" x2="1" y2="0">
                                <stop offset="0" stop-color="#f2681f" />
                                <stop offset=".55" stop-color="#e13a5c" />
                                <stop offset="1" stop-color="#b8399f" />
                            </linearGradient>
                            <symbol :id="uid('plane')" viewBox="-19.5 -18.5 39 37" overflow="visible">
                                <!-- motores (la parte trasera queda bajo el ala) -->
                                <g transform="translate(0 -5.75)"><rect x="1.9" y="-1.15" width="4.7" height="2.3" rx="1" :fill="url('eng')" /><rect x="6.1" y="-1.15" width=".6" height="2.3" rx=".3" fill="#5b6675" /></g>
                                <g transform="translate(0 5.75)"><rect x="1.9" y="-1.15" width="4.7" height="2.3" rx="1" :fill="url('eng')" /><rect x="6.1" y="-1.15" width=".6" height="2.3" rx=".3" fill="#5b6675" /></g>
                                <!-- alas en flecha con quiebre interior -->
                                <path :fill="url('wing')" d="M6.8 -1.5 L-1.6 -17.8 L-3.3 -17.8 L-2.4 -6.3 L-.6 -1.5 L-.6 1.5 L-2.4 6.3 L-3.3 17.8 L-1.6 17.8 L6.8 1.5 Z" />
                                <!-- winglets con el degradado de marca -->
                                <path :fill="url('brand')" d="M-1.6 -17.8 L-3.3 -17.8 L-3.6 -18.5 L-2.2 -18.5 Z" />
                                <path :fill="url('brand')" d="M-1.6 17.8 L-3.3 17.8 L-3.6 18.5 L-2.2 18.5 Z" />
                                <!-- pilones de los motores -->
                                <rect x="2.4" y="-6.15" width="2.5" height=".8" rx=".2" fill="#151b23" />
                                <rect x="2.4" y="5.35" width="2.5" height=".8" rx=".2" fill="#151b23" />
                                <!-- estabilizador horizontal -->
                                <path :fill="url('tail')" d="M-14.6 -.9 L-17.9 -6.2 L-19.2 -6.2 L-18.6 -.9 L-18.6 .9 L-19.2 6.2 L-17.9 6.2 L-14.6 .9 Z" />
                                <!-- fuselaje -->
                                <path :fill="url('fus')" d="M19 0 C19 -1.15 17.1 -1.95 14.5 -1.98 L-7 -1.98 C-11 -1.9 -15.5 -1.3 -18.8 -.55 L-18.8 .55 C-15.5 1.3 -11 1.9 -7 1.98 L14.5 1.98 C17.1 1.95 19 1.15 19 0 Z" />
                                <!-- cabina y brillo del lomo -->
                                <path fill="#0a0e13" opacity=".9" d="M16.9 -1.45 L15.6 -1.75 L15.6 1.75 L16.9 1.45 C17.2 .8 17.2 -.8 16.9 -1.45 Z" />
                                <path fill="#7d8896" opacity=".35" d="M14 -.55 L-8 -.55 C-11.5 -.5 -15 -.3 -17.5 0 L-17.5 .12 C-15 -.1 -11.5 -.25 -8 -.28 L14 -.28 Z" />
                            </symbol>
                        </defs>
                    </svg>

                    <!-- Avión 1: va por arriba, de la H a la s -->
                    <div ref="shadowLayer1" class="hyf-loader__layer">
                        <div ref="shadow1" class="hyf-loader__shadow">
                            <svg class="hyf-loader__plane-top" viewBox="0 0 39 37"><use :href="'#' + uid('plane')" /></svg>
                        </div>
                    </div>
                    <div ref="planeLayer1" class="hyf-loader__layer">
                        <div ref="plane1" class="hyf-loader__plane">
                            <svg class="hyf-loader__plane-top" viewBox="0 0 39 37"><use :href="'#' + uid('plane')" /></svg>
                            <div class="hyf-loader__fin-wrap">
                                <svg class="hyf-loader__fin" viewBox="0 0 10.5 6.5">
                                    <path :fill="url('brand')" d="M10.5 6.5 C9.4 6.3 8.6 5.7 8.1 5 L4.1 .45 C3.9 .22 3.6 .15 3.3 .15 L1.4 .15 L1.9 6.5 Z" />
                                    <path fill="#0a0e13" opacity=".22" d="M1.4 .15 L1.9 6.5 L2.9 6.5 L2.6 .15 Z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <!-- Avión 2: mismo recorrido girado 180°, por debajo -->
                    <div ref="shadowLayer2" class="hyf-loader__layer">
                        <div ref="shadow2" class="hyf-loader__shadow">
                            <svg class="hyf-loader__plane-top" viewBox="0 0 39 37"><use :href="'#' + uid('plane')" /></svg>
                        </div>
                    </div>
                    <div ref="planeLayer2" class="hyf-loader__layer">
                        <div ref="plane2" class="hyf-loader__plane">
                            <svg class="hyf-loader__plane-top" viewBox="0 0 39 37"><use :href="'#' + uid('plane')" /></svg>
                            <div class="hyf-loader__fin-wrap">
                                <svg class="hyf-loader__fin" viewBox="0 0 10.5 6.5">
                                    <path :fill="url('brand')" d="M10.5 6.5 C9.4 6.3 8.6 5.7 8.1 5 L4.1 .45 C3.9 .22 3.6 .15 3.3 .15 L1.4 .15 L1.9 6.5 Z" />
                                    <path fill="#0a0e13" opacity=".22" d="M1.4 .15 L1.9 6.5 L2.9 6.5 L2.6 .15 Z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="message || slotConTexto()" class="hyf-loader__msg">
                <slot>{{ message }}</slot>
            </div>
        </div>
    </div>
    </div>
</template>

<script>
import { LOGO_PATHS } from './hyfLogoPaths';

// Cantidad de loaders de pantalla completa abiertos (para bloquear el scroll).
let fullscreenOpen = 0;
// Cantidad de loaders en modo página abiertos (ocultan footer y bot).
let pageOpen = 0;
let seq = 0;

// Dentro de estos contenedores el loader no pasa a modo página.
const DIALOG_SEL = '[role="dialog"], [aria-modal="true"], .modal, .modal-content';

/* Vuelo: elipse alrededor del logo con ondulación suave, velocidad
   constante (longitud de arco), rumbo continuo y alabeo hacia el interior
   de la curva. Mismos valores que la demo aprobada. */
const FLY_DELAY = 150;
const LAP_MS = 5500;
const RX = 0.54, RY = 0.66, WAVES = 3, WAVE_AMP = 0.06;
const CAM = 34, BANK_MAX = 34, BANK_GAIN = 120, BANK_TAU = 150;
const ENTER_MS = 450, SHADOW_DX = 8, SHADOW_DY = 13;

function rawOrbit(u, W, H) {
    const th = Math.PI + u * Math.PI * 2;
    const r = 1 + WAVE_AMP * Math.sin(u * Math.PI * 2 * WAVES + 0.9);
    return { x: W * (0.5 + RX * r * Math.cos(th)), y: H * (0.5 + RY * r * Math.sin(th)) };
}
function wrapDeg(d) { return d - 360 * Math.round(d / 360); }

export default {
    name: 'HyFLoader',
    props: {
        // fullscreen | overlay | inline
        variant: {
            type: String,
            default: 'fullscreen',
            validator: v => ['fullscreen', 'overlay', 'inline'].indexOf(v) !== -1
        },
        message: { type: String, default: '' },
        // Sin aviones (por ejemplo, cuando el loader muestra un error).
        paused: { type: Boolean, default: false },
        ariaLabel: { type: String, default: 'Cargando' },
        // Se queda dentro de su caja (no pasa a modo página). Para cargas
        // chicas dentro de una página que ya se está mostrando.
        local: { type: Boolean, default: false }
    },
    data() {
        return {
            logoPaths: LOGO_PATHS,
            reduceMotion: false,
            // Se decide de entrada para no mostrar un cuadro con el footer
            // visible; en mounted se revisa si está dentro de un diálogo.
            page: !this.local,
            headerBottom: 0
        };
    },
    computed: {
        // La línea blanca corre mientras carga; en pausa (un resultado) el logo queda naranja liso.
        brilla() { return !this.paused && !this.reduceMotion; }
    },
    created() {
        this.idBase = 'hyf-ld' + (++seq) + '-';
    },
    mounted() {
        this.reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.page && this.$el.closest && this.$el.closest(DIALOG_SEL)) this.page = false;
        if (this.variant === 'fullscreen' && !this.page) {
            if (fullscreenOpen++ === 0) document.documentElement.classList.add('hyf-loader-lock');
        }
        if (this.page) this.enterPage();
        this.startFlight();
    },
    beforeDestroy() {
        cancelAnimationFrame(this.raf);
        if (this.variant === 'fullscreen' && !this.page) {
            if (--fullscreenOpen === 0) document.documentElement.classList.remove('hyf-loader-lock');
        }
        if (this.variant === 'fullscreen') this.curtain();
        if (this.page) this.leavePage();
    },
    watch: {
        paused(p) {
            if (!p) this.$nextTick(this.startFlight);
            else cancelAnimationFrame(this.raf);
        }
    },
    methods: {
        uid(name) { return this.idBase + name; },
        // El slot puede venir vacío (un v-if en falso deja solo un comentario).
        slotConTexto() {
            return (this.$slots.default || []).some(n => n.tag || (n.text && n.text.trim()));
        },
        url(name) { return 'url(#' + this.uid(name) + ')'; },

        /* Modo página: el loader queda fijo debajo del header (que sigue a la
           vista y usable) y <html> recibe la clase que oculta footer y bot. */
        enterPage() {
            if (pageOpen++ === 0) document.documentElement.classList.add('hyf-page-loading');
            const box = this.$refs.box;
            if (box && document.body) document.body.appendChild(box);
            this.measureHeader();
            this.onResize = () => this.measureHeader();
            window.addEventListener('resize', this.onResize);
            const header = document.querySelector('.app-header');
            if (header && window.ResizeObserver) {
                this.headerRO = new ResizeObserver(this.onResize);
                this.headerRO.observe(header);
            }
        },
        leavePage() {
            if (--pageOpen <= 0) {
                pageOpen = 0;
                document.documentElement.classList.remove('hyf-page-loading');
            }
            window.removeEventListener('resize', this.onResize);
            if (this.headerRO) this.headerRO.disconnect();
            // La caja está en <body>: Vue solo quita el ancla, así que se
            // saca acá. Inline/overlay se desvanecen; pantalla completa ya
            // dejó su telón (curtain).
            const box = this.$refs.box;
            if (!box || !box.parentNode) return;
            if (this.variant === 'fullscreen' || this.reduceMotion) { box.parentNode.removeChild(box); return; }
            box.classList.add('is-page-out');
            box.setAttribute('aria-hidden', 'true');
            box.removeAttribute('role');
            setTimeout(() => { if (box.parentNode) box.parentNode.removeChild(box); }, 320);
        },
        measureHeader() {
            const header = document.querySelector('.app-header');
            const r = header && header.offsetParent !== null ? header.getBoundingClientRect() : null;
            this.headerBottom = r ? Math.max(0, Math.round(r.bottom)) : 0;
        },

        startFlight() {
            if (this.paused || this.reduceMotion || !this.$refs.plane1) return;
            this.craft = [
                { plane: this.$refs.plane1, shadow: this.$refs.shadow1, planeLayer: this.$refs.planeLayer1, shadowLayer: this.$refs.shadowLayer1, flip: false },
                { plane: this.$refs.plane2, shadow: this.$refs.shadow2, planeLayer: this.$refs.planeLayer2, shadowLayer: this.$refs.shadowLayer2, flip: true }
            ];
            this.arc = null;
            this.arcKey = '';
            this.bank = 0;
            this.lastNow = 0;
            this.t0 = performance.now();
            cancelAnimationFrame(this.raf);
            const tick = (now) => { this.fly(now); this.raf = requestAnimationFrame(tick); };
            this.raf = requestAnimationFrame(tick);
        },

        buildArc(W, H) {
            const N = 720, len = [0];
            let total = 0, prev = rawOrbit(0, W, H);
            for (let i = 1; i <= N; i++) {
                const p = rawOrbit(i / N, W, H);
                total += Math.hypot(p.x - prev.x, p.y - prev.y);
                len.push(total);
                prev = p;
            }
            this.arc = { N, len, total };
        },
        orbit(tau, W, H) {
            const key = W + 'x' + H;
            if (key !== this.arcKey) { this.buildArc(W, H); this.arcKey = key; }
            tau -= Math.floor(tau);
            const { len, total, N } = this.arc;
            const s = tau * total;
            let lo = 0, hi = N;
            while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (len[mid] <= s) lo = mid; else hi = mid; }
            const seg = len[hi] - len[lo];
            const f = seg > 0 ? (s - len[lo]) / seg : 0;
            return rawOrbit((lo + f) / N, W, H);
        },
        heading(tau, W, H) {
            const a = this.orbit(tau - 0.002, W, H), b = this.orbit(tau + 0.002, W, H);
            return Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
        },

        fly(now) {
            const craft = this.craft;
            if (!craft || !craft[0].planeLayer) return;
            const el = now - this.t0 - FLY_DELAY;
            const dt = this.lastNow ? Math.min(50, now - this.lastNow) : 16;
            this.lastNow = now;
            if (el < 0) {
                craft.forEach(c => { c.planeLayer.style.opacity = 0; c.shadowLayer.style.opacity = 0; });
                this.bank = 0;
                return;
            }
            const wrap = craft[0].planeLayer.parentNode;
            const W = wrap.clientWidth, H = wrap.clientHeight;
            if (!W || !H) return;
            const tau = el / LAP_MS;
            const p = this.orbit(tau, W, H);
            const h = this.heading(tau, W, H);

            // Alabeo proporcional a la velocidad de giro, hacia dentro de la curva, suavizado.
            const turn = wrapDeg(this.heading(tau + 0.01, W, H) - this.heading(tau - 0.01, W, H)) / (0.02 * LAP_MS);
            const target = Math.max(-BANK_MAX, Math.min(BANK_MAX, -turn * BANK_GAIN));
            this.bank += (target - this.bank) * (1 - Math.exp(-dt / BANK_TAU));

            // Entrada: llega "de lejos" (pequeño y transparente) y toma tamaño.
            const e = 1 - Math.pow(1 - Math.min(1, el / ENTER_MS), 3);
            const bob = 1 + 0.035 * Math.sin(tau * Math.PI * 2 * WAVES + 2.1);
            const s = (0.55 + 0.45 * e) * bob;
            const bank = this.bank;

            craft.forEach(c => {
                const x = c.flip ? W - p.x : p.x, y = c.flip ? H - p.y : p.y, hd = c.flip ? h + 180 : h;
                c.plane.style.transform =
                    'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,0) ' +
                    'rotateX(' + CAM + 'deg) rotateZ(' + hd.toFixed(2) + 'deg) rotateX(' + bank.toFixed(2) + 'deg) ' +
                    'scale3d(' + s.toFixed(3) + ',' + s.toFixed(3) + ',' + s.toFixed(3) + ')';
                c.planeLayer.style.opacity = e;

                const ss = s * 0.94, sx = x + SHADOW_DX * s, sy = y + SHADOW_DY * s;
                c.shadow.style.transform =
                    'translate3d(' + sx.toFixed(2) + 'px,' + sy.toFixed(2) + 'px,0) ' +
                    'rotateX(' + CAM + 'deg) rotateZ(' + hd.toFixed(2) + 'deg) ' +
                    'scale3d(' + ss.toFixed(3) + ',' + ss.toFixed(3) + ',' + ss.toFixed(3) + ')';
                c.shadowLayer.style.opacity = 0.2 * e;
            });
        },

        /* Telón de salida (solo pantalla completa). El padre quita el
           componente con v-if, así que se deja una copia fija encima: primero
           se desvanece el logo y después la copia sube y deja ver la página. */
        curtain() {
            const el = this.$refs.box;
            if (!el || this.reduceMotion || !document.body) return;
            const copy = el.cloneNode(true);
            copy.classList.add('is-curtain');
            copy.removeAttribute('role');
            copy.setAttribute('aria-hidden', 'true');
            document.body.appendChild(copy);
            void copy.offsetWidth;
            copy.classList.add('is-done');
            setTimeout(() => copy.classList.add('is-leaving'), 160);
            setTimeout(() => { if (copy.parentNode) copy.parentNode.removeChild(copy); }, 750);
        }
    }
};
</script>

<style>
/* Global a propósito: el bloqueo de scroll va en <html>. */
html.hyf-loader-lock, html.hyf-loader-lock body { overflow: hidden; }

/* Modo página: mientras carga, solo el header y el loader. El footer (nuevo
   y viejo) y el botón de WhatsApp no se muestran ni ocupan lugar. */
html.hyf-page-loading, html.hyf-page-loading body { overflow: hidden; }
html.hyf-page-loading footer.footer,
html.hyf-page-loading #whatsapp-chat,
html.hyf-page-loading .whatsapp-chat,
html.hyf-page-loading .cookie-footer { display: none !important; }
</style>

<style scoped>
.hyf-loader {
    --l-ink: #080b0f;
    --l-muted: #4b5b63;
    --l-grad-a: #6d3ce0;
    --l-grad-b: #b8399f;
    --l-grad-c: #f2681f;
    --l-ease: cubic-bezier(.22, .61, .36, 1);
    --l-ease-out: cubic-bezier(.16, 1, .3, 1);
    --l-logo-h: clamp(72px, 12vw, 130px);
    --m: 1.65; /* píxeles por metro del avión */

    display: grid;
    place-items: center;
    overflow: hidden;
    isolation: isolate;
    color: var(--l-ink);
    box-sizing: border-box;
}

/* Pantalla completa: delante de todo, como en la demo */
.hyf-loader--fullscreen {
    position: fixed;
    top: 0; right: 0; bottom: 0; left: 0;
    z-index: 3000;
    background: #fff;
    transition: transform .5s var(--l-ease), border-radius .5s var(--l-ease);
    will-change: transform;
}
.hyf-loader--fullscreen.is-curtain { pointer-events: none; z-index: 3001; }
.hyf-loader--fullscreen.is-leaving { transform: translateY(-100%); border-radius: 0 0 44px 44px; }

/* Tapa solo al contenedor padre */
.hyf-loader--overlay {
    position: absolute;
    top: 0; right: 0; bottom: 0; left: 0;
    z-index: 50;
    min-height: 320px;
    background: #fff;
}

/* Bloque dentro de la página (listas, paneles) */
.hyf-loader--inline {
    position: relative;
    width: 100%;
    min-height: 300px;
    padding: 48px 16px;
    --l-logo-h: clamp(48px, 7vw, 84px);
    --m: 1.1;
}

/* Modo página (inline u overlay fuera de un diálogo): fijo debajo del header,
   tapa el resto del visor. z-index justo debajo del header (999) para que
   sus menús queden encima. */
.hyf-loader.is-page {
    position: fixed;
    top: var(--hyf-ld-top, 0px); right: 0; bottom: 0; left: 0;
    z-index: 998;
    width: auto;
    max-width: none;
    min-height: 0;
    padding: 0;
    margin: 0;
    flex: none;
    background: #fff;
    --l-logo-h: clamp(72px, 12vw, 130px);
    --m: 1.65;
}
/* Salida del modo página: se desvanece y deja ver la página ya cargada */
.hyf-loader.is-page-out {
    pointer-events: none;
    opacity: 0;
    transition: opacity .3s var(--l-ease);
}
.hyf-loader-anchor { display: contents; }

.hyf-loader__stage {
    position: relative;
    display: grid;
    justify-items: center;
    transition: transform .3s var(--l-ease), opacity .25s var(--l-ease);
}
.hyf-loader.is-done .hyf-loader__stage { transform: translateY(-10px) scale(1.04); opacity: 0; }

/* Luces de marca muy tenues detrás del logo. Sin filter: blur, que en
   pantallas grandes dejaba un borde recto visible; el degradado ya es suave. */
.hyf-loader__glow {
    position: absolute;
    z-index: -1;
    width: 62vmax; height: 62vmax;
    border-radius: 50%;
    pointer-events: none;
}
.hyf-loader__glow--a { background: radial-gradient(circle, rgba(109, 60, 224, .13), transparent 62%); top: -28vmax; left: -22vmax; }
.hyf-loader__glow--c { background: radial-gradient(circle, rgba(242, 104, 31, .14), transparent 62%); bottom: -30vmax; right: -20vmax; }

/* Logo naranja (SVG): la línea blanca la anima el propio SVG, así que acá
   solo la entrada. */
.hyf-loader__logo {
    display: block;
    height: var(--l-logo-h);
    width: calc(var(--l-logo-h) * 3.489); /* proporción del viewBox (471 / 135) */
    max-width: none;
    overflow: visible;
    animation: hyf-l-fade-up .35s var(--l-ease-out) both;
}

/* Fondo de marca fijo para el modo página: los tonos de HyFFondo sin blur ni
   animación (se pinta una sola vez). */
.hyf-loader__bg {
    position: absolute;
    top: 0; right: 0; bottom: 0; left: 0;
    z-index: -1;
    pointer-events: none;
    background:
        radial-gradient(40vmax 40vmax at 30% -6%, rgba(109, 60, 224, .16), transparent 70%),
        radial-gradient(38vmax 38vmax at 104% 34%, rgba(184, 57, 159, .13), transparent 70%),
        radial-gradient(40vmax 40vmax at 55% 108%, rgba(242, 104, 31, .17), transparent 70%),
        #fff;
}

/* Texto debajo del logo */
.hyf-loader__msg {
    margin-top: calc(var(--l-logo-h) * .7);
    max-width: 520px;
    padding: 0 16px;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    color: var(--l-muted);
    text-align: center;
    animation: hyf-l-fade-up .3s var(--l-ease-out) both .1s;
}

/* ---------- Avión 3D: vista cenital + deriva levantada con CSS ---------- */
.hyf-loader__mark-wrap { position: relative; }
.hyf-loader__flight {
    position: absolute; left: 0; top: 0;
    width: 100%; height: 100%;
    pointer-events: none;
    /* Sin filtros: el halo blanco (triple drop-shadow) se recalculaba en cada
       cuadro y era lo más caro del loader. */
}
.hyf-loader__defs { position: absolute; width: 0; height: 0; overflow: hidden; }
/* Capa por elemento: lleva la opacidad y la perspectiva, así el avión
   conserva su 3D (opacity/will-change aplanarían la deriva). */
.hyf-loader__layer {
    position: absolute; top: 0; right: 0; bottom: 0; left: 0;
    perspective: 700px;
    opacity: 0;
    will-change: opacity;
}
.hyf-loader__plane, .hyf-loader__shadow {
    position: absolute; left: 0; top: 0;
    width: 0; height: 0;
    transform-style: preserve-3d;
    will-change: transform;
}
/* Sombra proyectada en el "suelo": la misma silueta, oscura (sin blur, que
   obligaba a repintar cada cuadro); la capa le da la transparencia. */
.hyf-loader__shadow { filter: brightness(0); }
.hyf-loader__plane-top {
    position: absolute;
    width: calc(var(--m) * 39px); height: calc(var(--m) * 37px);
    left: calc(var(--m) * -19.5px); top: calc(var(--m) * -18.5px);
    overflow: visible;
}
.hyf-loader__fin-wrap {
    position: absolute; left: 0; top: 0; width: 0; height: 0;
    transform-style: preserve-3d;
    transform: rotateX(-90deg);
}
.hyf-loader__fin {
    position: absolute;
    width: calc(var(--m) * 10.5px); height: calc(var(--m) * 6.5px);
    left: calc(var(--m) * -19.5px); top: calc(var(--m) * -6.5px);
    overflow: visible;
}

@keyframes hyf-l-fade-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

@media (prefers-reduced-motion: reduce) {
    .hyf-loader__logo { animation: none !important; }
    .hyf-loader__flight { display: none; }
    .hyf-loader--fullscreen, .hyf-loader__stage { transition-duration: .2s; }
}
</style>
